/**
 * T-0894: the desktop runtime's commands, loaded on the first call. `desktopRuntime.ts` is the whole
 * shell command surface (1,700 lines, about 11 KB of the browser's first-screen entry), and a browser
 * tab never calls it: every caller sits behind `isDesktopRuntime()`. A file the first screen reaches
 * imports its commands from here, so the runtime is its own chunk, fetched by the desktop shell alone.
 * `desktopRuntimeLazy.guard.test.ts` keeps the first screen's files from importing the runtime itself.
 *
 * Only commands that already return a promise are listed: a call waits one more turn for the chunk,
 * and a caller that needs the answer at once could not have it from a Tauri `invoke` either.
 */
type RuntimeType = typeof import('./desktopRuntime');

let loading: Promise<RuntimeType> | null = null;

function runtime(): Promise<RuntimeType> {
  loading ??= import('./desktopRuntime');
  return loading;
}

type PromisedCommandNameType = {
  [K in keyof RuntimeType]: RuntimeType[K] extends (...args: never[]) => Promise<unknown>
    ? K
    : never;
}[keyof RuntimeType];

function lazy<K extends PromisedCommandNameType>(name: K): RuntimeType[K] {
  const call = (...args: unknown[]) =>
    runtime().then((module) => (module[name] as (...rest: unknown[]) => unknown)(...args));
  return call as unknown as RuntimeType[K];
}

export const getDesktopCapabilities = lazy('getDesktopCapabilities');
export const reportLaunchSmokeMilestone = lazy('reportLaunchSmokeMilestone');
export const readDeploymentIdentityNatively = lazy('readDeploymentIdentityNatively');
export const runPasskeySheet = lazy('runPasskeySheet');
export const listDesktopWorkAreaBindings = lazy('listDesktopWorkAreaBindings');
export const requestDesktopCloudApi = lazy('requestDesktopCloudApi');
export const subscribeDesktopCloudRealtime = lazy('subscribeDesktopCloudRealtime');
export const reportDesktopBusy = lazy('reportDesktopBusy');
export const getMachineReportingConsent = lazy('getMachineReportingConsent');
export const getDesktopTerminalMachine = lazy('getDesktopTerminalMachine');
export const getTerminalWatchBrowserStatus = lazy('getTerminalWatchBrowserStatus');
export const getTerminalWatchLimit = lazy('getTerminalWatchLimit');
export const setTerminalWatchLimit = lazy('setTerminalWatchLimit');
export const openDesktopLanSocket = lazy('openDesktopLanSocket');
export const sendDesktopLanSocket = lazy('sendDesktopLanSocket');
export const closeDesktopLanSocket = lazy('closeDesktopLanSocket');
